"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Image from "next/image";
import { Header } from "@/components/header";
import { HeroSection } from "@/components/hero-section";
import { Guide } from "@/data/guides-data";
import { useState, useEffect, useRef } from "react";
import { useInView } from "@/hooks/useInView";
import { useLanguage } from "@/context/LanguageContext";
import { contentInfoApi } from "@/lib/api";

import dynamic from "next/dynamic";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

export default function Guides() {
  const { language } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [guidesRef, guidesInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroContent, setHeroContent] = useState<{
    content_title: string;
    content_description?: string | null;
  } | null>(null);

  const datePickerRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 3;
  const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

  const labels = {
    en: {
      allDates: "All Dates",
      dateRange: "Date Range",
      startDate: "Start Date",
      endDate: "End Date",
      clear: "Clear",
      lastUpdate: "*Last update:",
      readGuide: "Read Guide",
      langFormat: "en-US",
      findGuide: "Find guide",
    },
    id: {
      allDates: "Semua Tanggal",
      dateRange: "Pilih Tanggal",
      startDate: "Tanggal Mulai",
      endDate: "Tanggal Akhir",
      clear: "Bersihkan",
      lastUpdate: "*Terakhir diperbarui:",
      readGuide: "Baca Panduan",
      langFormat: "id-ID",
      findGuide: "Cari panduan",
    },
  };

  const t = labels[language];

  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "guide", lang: language });
        const data = (response.data?.data || response.data) as Array<{
          content_title: string;
          content_description?: string | null;
        }>;

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching guides hero content:", err);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  // Fetch guides from backend
  useEffect(() => {
    const fetchGuides = async () => {
      try {
        setLoading(true);
        const url = new URL(`${API_BASE_URL}/guides/`);
        if (language) {
          url.searchParams.append('lang', language);
        }
        const response = await fetch(url.toString());

        if (!response.ok) {
          throw new Error('Failed to fetch guides');
        }

        const data: Guide[] = await response.json();
        setGuides(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.log('Error fetching guides:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, [API_BASE_URL, language]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter guides based on search and date
  const filteredGuides = guides.filter(guide => {
    const matchesSearch = searchQuery === "" ||
      guide.guides_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (guide.guides_description && guide.guides_description.toLowerCase().includes(searchQuery.toLowerCase()));

    // Date filtering logic based on updated_at
    let matchesDate = true;
    if (startDate || endDate) {
      const guideDate = new Date(guide.updated_at);
      if (startDate && guideDate < new Date(startDate)) matchesDate = false;
      if (endDate && guideDate > new Date(endDate)) matchesDate = false;
    }

    return matchesSearch && matchesDate;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredGuides.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentGuides = filteredGuides.slice(startIndex, endIndex);
  const pagesToShow = [];

  // Pagination logic
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) {
      pagesToShow.push(i);
    }
  } else {
    if (currentPage <= 3) {
      pagesToShow.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pagesToShow.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pagesToShow.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
  }

  const handleReadGuide = (guideUrl: string | null) => {
    if (!guideUrl) {
      alert('Guide URL not available');
      return;
    }
    // Open PDF in new tab with PDF viewer
    window.open(guideUrl, '_blank');
  };

  // Format date to readable format
  const formatDate = (dateString: string) => {
    const locale = t.langFormat;
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        {/* Navigation Bar */}
        <Header activePage="research" />

        {/* Hero Section */}
        <HeroSection
          title={heroContent?.content_title || "Guides & Tutorials"}
          subtitle={heroContent?.content_description || "Explore how to use our products effectively with step-by-step guides and tutorials."}
        />

        {/* Guides Content Section */}
        <section className="py-12 px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Search and Filter Bar */}
            <div
              ref={contentRef}
              className={`relative z-30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 reveal ${contentInView ? "is-visible" : ""}`}
            >

              {/* Search Input */}
              <div className="relative w-full md:w-auto">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <Input
                  type="text"
                  placeholder={t.findGuide}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-64"
                />
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-3">
                <div className="relative" ref={datePickerRef}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsDatePickerOpen(!isDatePickerOpen);
                    }}
                    className="flex items-center gap-2 border-gray-300 hover:bg-gray-50 bg-white"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{startDate || endDate ? t.dateRange : t.allDates}</span>
                  </Button>
                  {isDatePickerOpen && (
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-300 rounded-md shadow-lg z-9999 p-4">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t.startDate}</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t.endDate}</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => {
                              setStartDate("");
                              setEndDate("");
                              setIsDatePickerOpen(false);
                            }}
                            variant="outline"
                            className="flex-1 text-sm"
                          >
                            {t.clear}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Guides List */}
            <div ref={guidesRef} className={`relative z-0 space-y-4 mb-12 reveal ${guidesInView ? 'is-visible reveal-delay-200' : ''}`}>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                  <p className="text-gray-500 text-lg mt-4">Loading guides...</p>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-500 text-lg">Error: {error}</p>
                </div>
              ) : currentGuides.length > 0 ? (
                currentGuides.map((guide) => (
                  <div
                    key={guide.id_guides}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Left Content */}
                      <div className="flex-1">
                        {/* Title */}
                        <h2 className="text-xl md:text-2xl font-bold text-black mb-2">
                          {guide.guides_title}
                        </h2>

                        {/* Description */}
                        {guide.guides_description && (
                          <p className="text-black mb-0 mt-3">
                            {guide.guides_description}
                          </p>
                        )}

                        {/* Last Update */}
                        <p className="text-sm text-gray-500 italic">
                          {t.lastUpdate} {formatDate(guide.updated_at)}
                        </p>
                      </div>

                      {/* Right Button */}
                      <div className="shrink-0">
                        <Button
                          onClick={() => handleReadGuide(guide.guides_url)}
                          disabled={!guide.guides_url}
                          className="rounded-[10px] px-6 py-2 text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)" }}
                        >
                          <span>{t.readGuide}</span>
                          {/* <Image src="/homepage/homepage-picture/arrow-right.svg"
                            alt="Arrow"
                            width={24}
                            height={24}
                            className="w-6 h-6"
                            loading="lazy" /> */}
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </Button>

                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No guides found matching your criteria.</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex justify-end items-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-md border border-gray-300 text-black hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {pagesToShow.map((page, idx) => (
                <button
                  key={`${page}-${idx}`}
                  onClick={() => typeof page === "number" && setCurrentPage(page)}
                  disabled={page === "..."}
                  className={`px-4 py-2 rounded-md border transition-colors ${page === currentPage
                    ? "bg-[#0C1A2E] text-white border-[#0C1A2E]"
                    : page === "..."
                      ? "border-gray-300 text-black cursor-default px-2"
                      : "border-gray-300 text-black hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50"
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-md border border-gray-300 text-black hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}

